import { Users } from 'lucide-react';
import { Link } from 'react-router';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { MEDIA_URL } from '@/config';
import { Matchdata } from '@/types/match';
import { convertUnixTimestamp } from '@/utils/unix';

import { MatchStatus } from '../types';

type Props = {
  match?: Matchdata;
  status?: MatchStatus;
};

const getStatusInfo = (matchStatus?: MatchStatus) => {
  switch (matchStatus) {
    case MatchStatus.UPCOMING:
      return {
        color: 'bg-[#FECC00]',
        text: 'Upcoming',
      };
    case MatchStatus.SERVER_LIVE:
    case MatchStatus.LIVE:
      return {
        color: 'bg-[#F62C50]',
        text: 'Live',
      };
    case MatchStatus.ENDED:
      return {
        color: 'bg-[#0BAEEA]',
        text: 'Finished',
      };
    default:
      return {
        color: 'bg-secondary',
        text: 'Unknown',
      };
  }
};

const Banner = (props: Props) => {
  if (props.match) {
    const isFF =
      props.match.winner_team_id !== 0 &&
      props.match.get5StatsMatch.get5StatsMaps.length === 0;
    const formatted = convertUnixTimestamp(props.match.timestamp);
    const statusInfo = getStatusInfo(props.status);
    return (
      <div className="bg-block relative z-0 flex overflow-hidden rounded-xl border border-border px-14 py-20 shadow-xs">
        <div className="flex w-full items-center gap-x-6 text-3xl font-semibold">
          <Avatar className="h-32 w-32">
            <AvatarImage
              src={`${MEDIA_URL}/images/uploads/${props.match.team1.team_photo}`}
              alt={props.match.team1.team_name || 'Team1 photo'}
            />
            <AvatarFallback className="bg-secondary">
              <Users />
            </AvatarFallback>
          </Avatar>
          <Link to={`/team/${props.match.team1.id}`}>
            {' '}
            {props.match.team1.team_name}
          </Link>
        </div>
        <div className="flex basis-3/5 flex-col justify-center gap-y-5 text-center">
          <div className="inline-flex items-center justify-center">
            <span
              className={`me-2 inline-block h-2 w-2 rounded-full ${statusInfo.color}`}
            ></span>
            <span className="text-[10px] font-semibold uppercase text-muted-foreground">
              {statusInfo.text}
            </span>
          </div>
          <div
            className={`flex justify-center ${
              isFF ? 'text-7xl' : 'text-8xl'
            } h-24 font-semibold tracking-tighter`}
          >
            <span
              className={`${
                props.match.winner_team_id.toString() == props.match.team1.id &&
                'text-[#0BEA21]'
              }`}
            >
              {isFF &&
              props.match.winner_team_id.toString() == props.match.team1.id ? (
                <div className={`mr-6 w-28 text-right`}>FFW</div>
              ) : isFF &&
                props.match.winner_team_id.toString() ==
                  props.match.team2.id ? (
                <div className={`mr-6 w-28 text-right`}>FFL</div>
              ) : props.match.get5StatsMatch && props.match.playoffs ? (
                <div className={`mr-6 w-28 text-right`}>
                  {props.match.get5StatsMatch.team1_score}
                </div>
              ) : props.match.get5StatsMatch &&
                props.match.get5StatsMatch.get5StatsMaps[0] ? (
                <div className={`mr-6 w-28 text-right`}>
                  {props.match.get5StatsMatch.get5StatsMaps[0].team1_score}
                </div>
              ) : (
                <div className={`mr-6 w-28 text-right`}>0</div>
              )}
            </span>
            <span
              className={`inline-block ${
                isFF ? 'leading-[62px]' : 'leading-[84px]'
              } text-muted-foreground`}
            >
              :
            </span>
            <span
              className={`${
                props.match.winner_team_id.toString() == props.match.team2.id &&
                'text-[#0BEA21]'
              }`}
            >
              {isFF &&
              props.match.winner_team_id.toString() == props.match.team2.id ? (
                <div className={`ml-6 w-28 text-left`}>FFW</div>
              ) : isFF &&
                props.match.winner_team_id.toString() ==
                  props.match.team1.id ? (
                <div className={`ml-6 w-28 text-left`}>FFL</div>
              ) : props.match.get5StatsMatch && props.match.playoffs ? (
                <div className={`ml-6 w-28 text-left`}>
                  {props.match.get5StatsMatch.team2_score}
                </div>
              ) : props.match.get5StatsMatch &&
                props.match.get5StatsMatch.get5StatsMaps[0] ? (
                <div className={`ml-6 w-28 text-left`}>
                  {props.match.get5StatsMatch.get5StatsMaps[0].team2_score}
                </div>
              ) : (
                <div className={`ml-6 w-28 text-left`}>0</div>
              )}
            </span>
          </div>
          <div className="mx-auto w-max rounded-lg border border-primary px-4 py-2 text-xs font-semibold text-muted-foreground">
            {formatted.date}
            <span className=""> {formatted.timeWithZone}</span>
          </div>
        </div>
        <div className="flex w-full flex-row-reverse items-center gap-x-6 text-3xl font-semibold">
          <Avatar className="h-32 w-32">
            <AvatarImage
              src={`${MEDIA_URL}/images/uploads/${props.match.team2.team_photo}`}
              alt={props.match.team1.team_name || 'Team2 photo'}
            />
            <AvatarFallback className="bg-secondary">
              <Users />
            </AvatarFallback>
          </Avatar>
          <Link to={`/team/${props.match.team2.id}`}>
            {' '}
            {props.match.team2.team_name}
          </Link>
        </div>
      </div>
    );
  }

  // Loading
  return (
    <div className="bg-block relative z-0 flex overflow-hidden border border-border px-14 py-20">
      <div className="flex w-full items-center gap-x-6 text-3xl font-semibold">
        <Skeleton className="h-32 w-32 rounded-full!" />
        <Skeleton className="h-7 w-72" />
      </div>
      <div className="flex basis-3/5 flex-col justify-center gap-y-5 text-center">
        <div className="inline-flex items-center justify-center">
          <Skeleton className="h-[15px] w-16" />
        </div>
        <div className="flex justify-center text-8xl font-semibold tracking-tighter">
          <div className="mr-6 w-28 text-right">0</div>
          <span className="inline-block leading-[84px] text-muted-foreground">
            :
          </span>
          <div className="ml-6 w-28 text-left">0</div>
        </div>
        <div className="mx-auto w-max rounded-lg border border-primary px-4 py-2 text-xs font-semibold text-muted-foreground">
          <Skeleton className="h-[15px] w-32" />
        </div>
      </div>
      <div className="flex w-full flex-row-reverse items-center gap-x-6 text-3xl font-semibold">
        <Skeleton className="h-32 w-32 rounded-full!" />
        <Skeleton className="h-7 w-72" />
      </div>
      <div
        className="absolute inset-x-0 -top-1/2 -z-10 transform-gpu overflow-hidden blur-3xl"
        aria-hidden="true"
      >
        <div className="bg-block relative left-0 aspect-1155/678 w-144.5 -translate-x-1/2 rotate-30 bg-linear-to-tr from-[#0BAEEA] to-[#d9d9d900] bg-blend-overlay sm:w-288.75"></div>
      </div>
      <div
        className="absolute inset-x-0 -top-1/2 -z-10 transform-gpu overflow-hidden blur-3xl"
        aria-hidden="true"
      >
        <div className="bg-block relative -right-[25%] aspect-1155/678 w-144.5 translate-x-1/2 rotate-[-30deg] bg-linear-to-tl from-[#0BAEEA] to-[#d9d9d900] bg-blend-overlay sm:w-288.75"></div>
      </div>
    </div>
  );
};

export default Banner;
